"use client";
import BodyLineText from "@/components/common/text/bodyline-text";
import HeadlineText from "@/components/common/text/headline-text";
import ScreenLayout from "@/components/layout/screen-layout";
import { Handshake, Rocket, Trophy, Zap } from "lucide-react";

const advantages = [
  {
    title: "Real Results That Matter",
    description:
      "We've helped over 200 businesses thrive with smart solutions, earning a 98% client happiness rate and boosting performance by 40% on average.",
    features: ["200+ Happy Clients", "98% Satisfaction Rate", "40% Performance Increase"],
    icon: Trophy,
    color: "text-blue-400",
  },
  {
    title: "Tech That's Ready for Tomorrow",
    description:
      "We use the latest AI, cloud technologies, and innovative tools to make sure your digital investments stay ahead of the curve.",
    features: ["AI-Powered Tools", "Cloud-Native Design", "Cutting-Edge Tech"],
    icon: Rocket,
    color: "text-purple-400",
  },
  {
    title: "Agile, Fast, and Flexible",
    description:
      "Our iterative process gets your product out the door 60% faster — all while keeping quality high and adapting as your needs change.",
    features: ["60% Quicker Launch", "Ongoing Improvements", "Flexible Development"],
    icon: Zap,
    color: "text-orange-400",
  },
  {
    title: "Your Full-Service Digital Partner",
    description:
      "From the first idea to scaling up, we're with you every step of the way, providing expert support and continuous improvement.",
    features: ["Complete Solutions", "Personalized Support", "Growth-Focused Approach"],
    icon: Handshake,
    color: "text-green-400",
  },
];

function WhyChooseUs() {
  return (
    <div className="md:pb-17.5 mt-17.5 pb-9">
      <ScreenLayout>
        {/* Section Header */}
        <div className="text-center mb-16 flex flex-col items-center justify-center">
          <HeadlineText>Why Leading Companies Choose Devkrest</HeadlineText>
          <BodyLineText>
            We don't just build software—we craft digital experiences that drive real business results.
            Here's why industry leaders trust us with their most critical digital initiatives.
          </BodyLineText>
        </div>

        {/* Advantages Grid */}
        <div className="grid lg:grid-cols-2 gap-6 mb-16">
          {advantages.map((advantage, index) => (
            <div key={index} className="group">
              <div className="bg-white border border-gray-100 rounded-xl p-6 hover:shadow-lg hover:border-gray-200 transition-all duration-300">
                {/* Header */}
                <div className="flex items-start gap-4 mb-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-transparent border border-gray-200 rounded-lg flex items-center justify-center">
                      <advantage.icon className={`w-5 h-5 ${advantage.color}`} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {advantage.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {advantage.description}
                    </p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-2">
                  {advantage.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <div className="w-1.5 h-1.5 bg-blue-600 rounded-full flex-shrink-0"></div>
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </ScreenLayout>
    </div>
  );
}

export default WhyChooseUs;
